import { AlertTriangle } from "lucide-react";

/**
 * PkiHalluzWertungBanner · Anzeige der "Wertung ohne Datenbasis"-Findings (P0-Halluz-Regel).
 *
 * Props:
 *   halluz · { regel, count, severity, label, findings: [{modul,fundstelle,erkannte_wertung,empfehlung,severity}] }
 *
 * Verhalten:
 *   findings=[] → null (keine Anzeige).
 *   findings>0 → Banner mit pro Finding einer Fundstelle, Wertung und Empfehlung.
 */
export default function PkiHalluzWertungBanner({ halluz }) {
  if (!halluz || !Array.isArray(halluz.findings) || halluz.findings.length === 0) return null;
  const rot = (halluz.severity || "rot") === "rot";
  const cls = rot
    ? "border-brand-darkred/45 bg-brand-darkred/8 text-brand-darkred"
    : "border-saffron/55 bg-saffron/15 text-clay-dark";

  return (
    <section
      data-testid="pki-halluz-wertung-banner"
      className={`rounded-2xl border ${cls} p-5 md:p-6 space-y-4`}
    >
      <header className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" strokeWidth={2.2} />
        <div className="space-y-1">
          <h3 className="text-[14px] font-bold tracking-[0.04em] uppercase">
            Halluzinationsschutz · Wertung ohne Datenbasis
          </h3>
          <p className="text-[13px] leading-relaxed">
            {halluz.label || "Wertungen ohne Datenbasis erkannt – bitte fachlich prüfen"}
            {" · "}
            <strong>{halluz.count}</strong>{" Treffer"}
          </p>
        </div>
      </header>

      <ul className="space-y-3" data-testid="pki-halluz-wertung-list">
        {halluz.findings.map((f, idx) => (
          <li
            key={idx}
            data-testid={`pki-halluz-wertung-item-${idx}`}
            className="rounded-xl border border-cream-200 bg-white/70 p-4 space-y-1.5"
          >
            <div className="flex flex-wrap items-baseline gap-2 text-[12px]">
              <span className="font-bold uppercase tracking-[0.08em] text-sand-700">
                {f.modul || "Modul ?"}
              </span>
              <span className="text-sand-500">·</span>
              <span className="text-sand-600 italic">
                Wertung „{f.erkannte_wertung}&ldquo; ohne Beleg
              </span>
            </div>
            <p className="text-[13px] text-sand-800 leading-snug">
              <span className="font-semibold">KI-Aussage:</span>{" "}
              <span className="italic">{f.fundstelle}</span>
            </p>
            {f.empfehlung && (
              <p className="text-[13px] text-sage-dark leading-snug">
                <span className="font-semibold">Empfehlung:</span> {f.empfehlung}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
